import { THE_PIT } from '../arenas';
import { SPARKY_BOT } from '../characters';
import { SKILL_BUNDLES } from '../skills';
import {
  BUFF_ICON_ASSETS,
  EFFECT_ICON_ASSETS,
  EFFECT_ROUND_ICON_ASSETS,
  PORTRAIT_TURN_SHEET_ASSET,
  RANK_ASSETS,
  UI_ASSETS,
  UI_AUDIO,
} from './uiAssets';

// Flattened, render-agnostic listing of every overridable game asset, derived
// from the same bundles the game loads (so it can never drift). The studio asset
// browser renders this; the `key` is the asset's bundled URL, which is also the
// override lookup key used by src/assets/overrides.ts.
//
// Note: importing the skill/arena bundles pulls their (pixi) render code into the
// studio chunk. Acceptable for an internal tool - keeping the catalog derived
// from real bundles is worth more than a smaller portal bundle.

export type AssetKind = 'sheet' | 'image' | 'audio';

export type CatalogEntry = {
  // Bundled URL = override lookup key.
  key: string;
  label: string;
  // Top-level section (one tab in the studio) e.g. "Character: SparkyBot".
  section: string;
  // Sub-catalog within the section e.g. "Animations" / "Particles" / "SFX" /
  // "Skill: cannon".
  subgroup: string;
  // Fine-grained group kept for the sprite picker (AssetPickerModal).
  group: string;
  kind: AssetKind;
  // Sheets only - the default grid (an override may change it).
  cols?: number;
  rows?: number;
};

function fileLabel(url: string): string {
  return url.split('/').pop() ?? url;
}

// Battle effect sprites (public/assets/textures/Effects). Not wired into a
// bundle yet - listed here so designers can manage them in the studio. Add one
// line per file; use kind 'sheet' with cols/rows if a file is a sprite sheet.
const EFFECTS_ROOT = '/assets/textures/Effects';
const EFFECTS_ASSETS: { url: string; label: string; kind?: AssetKind; cols?: number; rows?: number }[] = [
  { url: `${EFFECTS_ROOT}/bam.png`, label: 'Bam', kind: 'sheet', cols: 4, rows: 4 },
];

function buildCatalog(): CatalogEntry[] {
  const entries: CatalogEntry[] = [];

  // Character: animation sheets, particles, sfx + its skills all live under one
  // section. (Phase 1 is single-character - all skills belong to SparkyBot.)
  const charSection = 'Sparky';
  if (SPARKY_BOT.assets) {
    for (const [slot, spec] of Object.entries(SPARKY_BOT.assets)) {
      if (!spec) continue;
      entries.push({
        key: spec.url,
        label: `${slot} (${fileLabel(spec.url)})`,
        section: charSection,
        subgroup: 'Animations',
        group: charSection,
        kind: 'sheet',
        cols: spec.cols,
        rows: spec.rows,
      });
    }
  }
  // Character particles + sfx.
  for (const p of SPARKY_BOT.particles?.damage ?? []) {
    entries.push({ key: p.url, label: fileLabel(p.url), section: charSection, subgroup: 'Particles', group: `${charSection} / particles`, kind: 'image' });
  }
  for (const url of Object.values(SPARKY_BOT.sfx ?? {})) {
    entries.push({ key: url, label: fileLabel(url), section: charSection, subgroup: 'SFX', group: `${charSection} / sfx`, kind: 'audio' });
  }

  // Skills - sub-catalogs under the character section.
  for (const [id, bundle] of SKILL_BUNDLES) {
    const group = `Skill: ${id}`;
    for (const spec of Object.values(bundle.assets?.sheets ?? {})) {
      entries.push({ key: spec.url, label: fileLabel(spec.url), section: charSection, subgroup: group, group, kind: 'sheet', cols: spec.cols, rows: spec.rows });
    }
    for (const url of Object.values(bundle.assets?.textures ?? {})) {
      entries.push({ key: url, label: fileLabel(url), section: charSection, subgroup: group, group, kind: 'image' });
    }
    for (const url of Object.values(bundle.assets?.sfx ?? {})) {
      entries.push({ key: url, label: fileLabel(url), section: charSection, subgroup: group, group, kind: 'audio' });
    }
  }

  // Arena.
  const arenaGroup = `Arena: ${THE_PIT.name}`;
  for (const spec of Object.values(THE_PIT.assets.sheets ?? {})) {
    entries.push({ key: spec.url, label: fileLabel(spec.url), section: arenaGroup, subgroup: arenaGroup, group: arenaGroup, kind: 'sheet', cols: spec.cols, rows: spec.rows });
  }
  for (const url of Object.values(THE_PIT.assets.textures ?? {})) {
    entries.push({ key: url, label: fileLabel(url), section: arenaGroup, subgroup: arenaGroup, group: arenaGroup, kind: 'image' });
  }

  // Desktop / meta UI.
  for (const a of UI_ASSETS) {
    entries.push({ key: a.url, label: `${a.label} (${fileLabel(a.url)})`, section: 'UI', subgroup: 'UI', group: 'UI', kind: 'image' });
  }
  // UI audio (gong etc) - same UI section, audio kind.
  for (const a of UI_AUDIO) {
    entries.push({ key: a.url, label: `${a.label} (${fileLabel(a.url)})`, section: 'UI', subgroup: 'UI', group: 'UI', kind: 'audio' });
  }
  // Rank badges - their own subgroup under the UI section.
  for (const a of RANK_ASSETS) {
    entries.push({ key: a.url, label: `${a.label} (${fileLabel(a.url)})`, section: 'UI', subgroup: 'Ranks', group: 'Ranks', kind: 'image' });
  }
  // Buff badges - their own subgroup under the UI section.
  for (const a of BUFF_ICON_ASSETS) {
    entries.push({ key: a.url, label: `${a.label} (${fileLabel(a.url)})`, section: 'UI', subgroup: 'Buffs', group: 'Buffs', kind: 'image' });
  }
  // Gear effect icons (square + round) - their own subgroup on the Effects tab.
  for (const a of [...EFFECT_ICON_ASSETS, ...EFFECT_ROUND_ICON_ASSETS]) {
    entries.push({ key: a.url, label: `${a.label} (${fileLabel(a.url)})`, section: 'Effects', subgroup: 'Gear effects', group: 'Gear effects', kind: 'image' });
  }
  // Portrait turn sheet (6x6) - left-panel portrait easter-egg animation.
  entries.push({
    key: PORTRAIT_TURN_SHEET_ASSET.url,
    label: `${PORTRAIT_TURN_SHEET_ASSET.label} (${fileLabel(PORTRAIT_TURN_SHEET_ASSET.url)})`,
    section: 'UI',
    subgroup: 'Portrait',
    group: 'Portrait',
    kind: 'sheet',
    cols: PORTRAIT_TURN_SHEET_ASSET.cols,
    rows: PORTRAIT_TURN_SHEET_ASSET.rows,
  });

  // Battle effects - their own top-level section/tab.
  for (const e of EFFECTS_ASSETS) {
    entries.push({
      key: e.url,
      label: `${e.label} (${fileLabel(e.url)})`,
      section: 'Effects',
      subgroup: 'Effects',
      group: 'Effects',
      kind: e.kind ?? 'image',
      cols: e.cols,
      rows: e.rows,
    });
  }

  // Website chrome (studio pages - not loaded by the game).
  entries.push({
    key: '/studio/ss-games-logo.png',
    label: 'Studio logo (ss-games-logo.png)',
    section: 'Website',
    subgroup: 'Website',
    group: 'Website',
    kind: 'image',
  });

  // Dedupe by key - some assets are reused across bundles (e.g. ThePit reuses
  // the cannon's cannonball.png), but one URL = one overridable asset. Keep the
  // first occurrence so it stays under its primary group.
  const seen = new Set<string>();
  const deduped: CatalogEntry[] = [];
  for (const e of entries) {
    if (seen.has(e.key)) continue;
    seen.add(e.key);
    deduped.push(e);
  }
  return deduped;
}

export const ASSET_CATALOG: readonly CatalogEntry[] = buildCatalog();

// Catalog grouped by `group`, preserving first-seen group order, for sectioned
// rendering in the studio (used by the sprite picker).
export function catalogByGroup(): { group: string; items: CatalogEntry[] }[] {
  const groups: { group: string; items: CatalogEntry[] }[] = [];
  const index = new Map<string, CatalogEntry[]>();
  for (const entry of ASSET_CATALOG) {
    let bucket = index.get(entry.group);
    if (!bucket) {
      bucket = [];
      index.set(entry.group, bucket);
      groups.push({ group: entry.group, items: bucket });
    }
    bucket.push(entry);
  }
  return groups;
}

export type CatalogSection = {
  section: string;
  subgroups: { subgroup: string; items: CatalogEntry[] }[];
};

// Two-level view: top-level sections, each split into sub-catalogs. Preserves
// first-seen order. Drives the Assets tab (one tab per section, sub-headers per
// subgroup).
export function catalogBySection(): CatalogSection[] {
  const sections: CatalogSection[] = [];
  const secIndex = new Map<string, CatalogSection>();
  const subIndex = new Map<string, CatalogEntry[]>();
  for (const entry of ASSET_CATALOG) {
    let sec = secIndex.get(entry.section);
    if (!sec) {
      sec = { section: entry.section, subgroups: [] };
      secIndex.set(entry.section, sec);
      sections.push(sec);
    }
    const subKey = `${entry.section} ${entry.subgroup}`;
    let items = subIndex.get(subKey);
    if (!items) {
      items = [];
      subIndex.set(subKey, items);
      sec.subgroups.push({ subgroup: entry.subgroup, items });
    }
    items.push(entry);
  }
  return sections;
}
